import React, { useContext, useRef, useEffect, useState, MouseEvent, } from 'react';
import { JobSearchContext } from '../../context';
import classNames from 'classnames';
import { useGetMobileFiltersCount, useScrollToResults } from '../../hooks';
import Alerts from '../Alerts/alerts';
import Tooltip from '../../../components/react/tooltip/tooltip';

import styles from './mobile_keywords_filter.module.scss';

interface Props {
  containerRef: {
    current: HTMLDivElement;
  }
}

const MobileKeywordsFilter = ({ containerRef }: Props) => {
  const {
    keywordUserInput,
    setKeywordUserInput,
    showAlerts,
    setShowAlerts,
    setKeyword,
    setPageNumber,
    userInfo: { isSignedIn },
    setIsMobileFiltersOpen,
    keywordsRef,
  } = useContext(JobSearchContext);
  const [containerHeight, setContainerHeight] = useState(0);

  const alertsRef = useRef(null);
  const mobileFiltersCount = useGetMobileFiltersCount();
  const scrollToResults = useScrollToResults();

  const toggleAlertStickyClass = () => {
    if (alertsRef.current) {
      if (keywordsRef.current.classList.contains(styles.noStickyKeyword)) {
        alertsRef.current.classList.add(styles.noStickyAlerts);
      } else {
        alertsRef.current.classList.remove(styles.noStickyAlerts);
      }
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      setContainerHeight(containerRef.current.offsetHeight);
    }
  }, [containerRef.current?.offsetHeight]);

  // When position sticky is triggered change container styles
  useEffect(() => {
    if (keywordsRef.current) {
      const callBack = () => {
        keywordsRef.current.classList.toggle(styles.noStickyKeyword);
        toggleAlertStickyClass();
      };

      const options = {
        rootMargin: '-1px 0px 0px 0px',
        threshold: [1],
      };

      const observer = new IntersectionObserver(callBack, options);
      observer.observe(keywordsRef.current);

      return () => {
        observer.unobserve(keywordsRef.current);
      };
    }
  }, []);

  const handleSearch = (event: MouseEvent) => {
    event.preventDefault();
    setPageNumber(1);
    // If the input value is empty, remove URL filter keywords (filter[keywords][])
    setKeyword(keywordUserInput.trim().length ? keywordUserInput : undefined);
    scrollToResults();
  };

  const handleClear = () => {
    setPageNumber(1);
    setKeywordUserInput('');
    setKeyword(undefined);
    scrollToResults();
  };

  const handleOpenFilters = () => {
    const footerElement = document.querySelector('footer');
    footerElement.classList.add('d-none');

    setIsMobileFiltersOpen(true);
  };

  const handleShowAlerts = () => {
    setShowAlerts(!showAlerts);
    toggleAlertStickyClass();
  };

  const alertsButton = (
    <Tooltip
      placement='top'
      message='For logged in users'
      show={isSignedIn ? false : undefined}
      id='alerts-button'
    >
      <button
        type='button'
        className='btn btn-outline-info'
        aria-controls='alerts-container'
        data-testid='alerts-button'
        onClick={handleShowAlerts}
        disabled={!isSignedIn}
      >
        <i className='fal icon-bell me-2' />
        <span>Alerts</span>
        <i
          className={classNames('fal toggle ms-2', {
            'icon-angle-down': !showAlerts,
            'icon-angle-up': showAlerts,
          })}
        />
      </button>
    </Tooltip>
  );

  return (
    <div
      className={styles.absoluteContainer}
      style={{
        height: containerHeight,
      }}
    >
      <div className={styles.container} data-testid='mobile-keywords-filter'>
        <div className={styles.fieldsContainer} ref={keywordsRef}>
          <div className='input-group'>
            <input
              data-testid='jobs-search-keywords-filter'
              className={`form-control ${styles.keywordsInput}`}
              type='text'
              name='keywords'
              aria-label='keyword search input'
              placeholder='e.g. Chief of party'
              value={keywordUserInput}
              onChange={({ target }) => setKeywordUserInput(target.value)}
            />
            <div
              className={styles.clearButton}
              onClick={handleClear}
              aria-label='clear keywords field button'
              data-testid='clear-keywords-button'
            >
              <i className='fal icon-times' />
            </div>
            <button
              className='btn btn-primary'
              aria-label='search button'
              type='button'
              data-testid='search-keywords-button'
              onClick={handleSearch}
            >
              <i className='fal icon-search text-white' />
            </button>
          </div>
          <div className={styles.buttonsContainer}>
            <button className='btn btn-outline-secondary' onClick={handleOpenFilters}>
              <i className='fal icon-filter me-2' />
              <span data-testid='filters-button-text'>
                Filters
                { mobileFiltersCount ? ` (${mobileFiltersCount})` : ''}
              </span>
            </button>
            {alertsButton}
          </div>
        </div>
        {isSignedIn && (
          <div className='position-relative'>
            <div className={styles.alertsContainer} ref={alertsRef}>
              <Alerts />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileKeywordsFilter;
